import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import api, { formatCurrency, formatDate } from "../lib/api";
import { toast } from "sonner";
import { 
  Plus, 
  Upload, 
  TrendingUp, 
  TrendingDown,
  Wallet,
  FileSpreadsheet
} from "lucide-react";

const FundManagement = () => {
  const { user, isAdmin } = useAuth();
  const [funds, setFunds] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(user?.id);
  const [showAddDialog, setShowAddDialog] = useState(false);
  // STARSEED-CAPMARKETS-116 — a rejected import must show the operator WHICH
  // rows failed and why. Previously the per-row reasons went only to the
  // server log, which the person able to fix the file never sees.
  const [importErrors, setImportErrors] = useState(null);
  const [formData, setFormData] = useState({
    user_id: "",
    amount: "",
    entry_type: "deposit",
    description: "",
    date: new Date().toISOString().split("T")[0]
  });

  const fetchData = useCallback(async () => {
    try {
      const targetUser = isAdmin && selectedUser ? selectedUser : user?.id;

      if (isAdmin) {
        const usersRes = await api.get("/users");
        setUsers(usersRes.data);
      }

      if (targetUser) {
        const [fundsRes, perfRes] = await Promise.all([
          api.get(`/funds/user/${targetUser}`),
          api.get(`/funds/performance/${targetUser}`)
        ]);
        setFunds(fundsRes.data);
        setPerformance(perfRes.data);
      }
    } catch (error) {
      toast.error("Failed to load fund data");
    } finally {
      setLoading(false);
    }
  }, [isAdmin, selectedUser, user?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddEntry = async (e) => {
    e.preventDefault();
    try {
      await api.post("/funds", {
        ...formData,
        user_id: formData.user_id || selectedUser || user?.id,
        amount: parseFloat(formData.amount)
      });
      toast.success("Fund entry added successfully");
      setShowAddDialog(false);
      setFormData({
        user_id: "",
        amount: "",
        entry_type: "deposit",
        description: "",
        date: new Date().toISOString().split("T")[0]
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add entry");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await api.post("/funds/import-csv", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setImportErrors(null);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      // The import is all-or-nothing: on any failure the server wrote nothing.
      // `detail` carries {message, rejected, failures:[{line, field, reason}]}.
      const detail = error.response?.data?.detail;
      if (detail && Array.isArray(detail.failures)) {
        setImportErrors(detail);
        toast.error(detail.message || "Import rejected — no entries were saved.");
      } else {
        setImportErrors(null);
        toast.error("Failed to import CSV");
      }
    } finally {
      // Let the operator re-upload the SAME filename after correcting it —
      // without this the change event never fires a second time.
      e.target.value = "";
    }
  };

  return (
    <DashboardLayout 
      title="Fund Tracking" 
      subtitle="Track deposits, withdrawals, gains and losses"
    >
      {/* Admin User Selector */}
      {isAdmin && (
        <Card className="bg-surface border-white/5 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Label className="text-platinum/80 font-semibold">View User:</Label>
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger className="w-64 bg-obsidian border-white/10" data-testid="user-selector">
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent className="bg-surface border-white/10">
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.full_name} ({u.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Performance Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <Card className="bg-surface border-white/5">
          <CardContent className="p-6">
            <Wallet className="text-gold mb-2" size={20} />
            <div className="text-2xl font-bold text-white tabular-nums">
              {formatCurrency(performance?.total_deposited || 0)}
            </div>
            <p className="text-sm text-platinum/70">Total Deposited</p>
          </CardContent>
        </Card>

        <Card className="bg-surface border-white/5">
          <CardContent className="p-6">
            <TrendingUp className="text-emerald mb-2" size={20} />
            <div className="text-2xl font-bold text-emerald tabular-nums">
              {formatCurrency(performance?.total_gains || 0)}
            </div>
            <p className="text-sm text-platinum/70">Total Gains</p>
          </CardContent>
        </Card>

        <Card className="bg-surface border-white/5">
          <CardContent className="p-6">
            <TrendingDown className="text-red-400 mb-2" size={20} />
            <div className="text-2xl font-bold text-red-400 tabular-nums">
              {formatCurrency(performance?.total_losses || 0)}
            </div>
            <p className="text-sm text-platinum/70">Total Losses</p>
          </CardContent>
        </Card>

        <Card className="bg-surface border-white/5">
          <CardContent className="p-6">
            <Wallet className="text-blue-400 mb-2" size={20} />
            <div className="text-2xl font-bold text-white tabular-nums">
              {formatCurrency(performance?.current_balance || 0)}
            </div>
            <p className="text-sm text-platinum/70">Current Balance</p>
          </CardContent>
        </Card>

        <Card className="bg-surface border-white/5">
          <CardContent className="p-6">
            <TrendingUp className={`mb-2 ${(performance?.performance_percentage || 0) >= 0 ? 'text-emerald' : 'text-red-400'}`} size={20} />
            <div className={`text-2xl font-bold tabular-nums ${(performance?.performance_percentage || 0) >= 0 ? 'text-emerald' : 'text-red-400'}`}>
              {(performance?.performance_percentage || 0) >= 0 ? '+' : ''}{(performance?.performance_percentage || 0).toFixed(2)}%
            </div>
            <p className="text-sm text-platinum/70">Performance</p>
          </CardContent>
        </Card>
      </div>

      {/* Actions & Table */}
      <Card className="bg-surface border-white/5">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-white font-serif">Fund Entries</CardTitle>
            <CardDescription>All deposits, withdrawals, gains and losses</CardDescription>
          </div>
          
          {isAdmin && (
            <div className="flex gap-2">
              <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-gold hover:bg-gold/90 text-navy-dark" data-testid="add-fund-entry">
                    <Plus size={16} className="mr-2" />
                    Add Entry
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-surface border-white/10" aria-describedby="add-fund-description">
                  <DialogHeader>
                    <DialogTitle className="text-white font-serif">Add Fund Entry</DialogTitle>
                    <p id="add-fund-description" className="sr-only">Form to add a new fund entry</p>
                  </DialogHeader>
                  <form onSubmit={handleAddEntry} className="space-y-4">
                    {isAdmin && (
                      <div className="space-y-2">
                        <Label className="text-gray-300">User</Label>
                        <Select 
                          value={formData.user_id || selectedUser} 
                          onValueChange={(v) => setFormData(prev => ({ ...prev, user_id: v }))}
                        >
                          <SelectTrigger className="bg-obsidian border-white/10">
                            <SelectValue placeholder="Select user" />
                          </SelectTrigger>
                          <SelectContent className="bg-surface border-white/10">
                            {users.map((u) => (
                              <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <Label className="text-gray-300">Entry Type</Label>
                      <Select 
                        value={formData.entry_type} 
                        onValueChange={(v) => setFormData(prev => ({ ...prev, entry_type: v }))}
                      >
                        <SelectTrigger className="bg-obsidian border-white/10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-surface border-white/10">
                          <SelectItem value="deposit">Deposit</SelectItem>
                          <SelectItem value="withdrawal">Withdrawal</SelectItem>
                          <SelectItem value="gain">Gain</SelectItem>
                          <SelectItem value="loss">Loss</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-gray-300">Amount ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.amount}
                        onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                        className="bg-white border-slate-200 text-slate-900"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-gray-300">Date</Label>
                      <Input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                        className="bg-white border-slate-200 text-slate-900"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-gray-300">Description (Optional)</Label>
                      <Input
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        className="bg-white border-slate-200 text-slate-900"
                        placeholder="e.g., Monthly contribution"
                      />
                    </div>

                    <Button type="submit" className="w-full bg-gold hover:bg-gold/90 text-navy-dark">
                      Add Entry
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              <label className="cursor-pointer">
                <input 
                  type="file" 
                  accept=".csv" 
                  className="hidden" 
                  onChange={handleFileUpload}
                />
                <Button variant="outline" className="border-white/10" asChild>
                  <span>
                    <FileSpreadsheet size={16} className="mr-2" />
                    Import CSV
                  </span>
                </Button>
              </label>
            </div>
          )}
        </CardHeader>
        {importErrors && (
          <div className="px-6 pb-4" data-testid="import-failure-panel">
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4">
              <p className="text-sm font-semibold text-red-400">
                {importErrors.message}
              </p>
              <p className="mt-1 text-xs text-gray-400">
                Nothing was saved. Correct the rows below and upload the file again.
              </p>
              <ul className="mt-3 space-y-1">
                {importErrors.failures.map((f, i) => (
                  <li key={`${f.line}-${f.field}-${i}`} className="text-xs text-platinum/80">
                    <span className="font-mono text-red-400">line {f.line}</span>
                    {f.field ? <span className="font-mono text-gray-400"> · {f.field}</span> : null}
                    <span> — {f.reason}</span>
                  </li>
                ))}
              </ul>
              <Button
                variant="outline"
                className="mt-3 border-white/10"
                onClick={() => setImportErrors(null)}
              >
                Dismiss
              </Button>
            </div>
          </div>
        )}
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="funds-table">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Type</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-400">Amount</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Description</th>
                </tr>
              </thead>
              <tbody>
                {funds.length > 0 ? funds.map((entry) => (
                  <tr key={entry.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="py-3 px-4 text-gray-400">{formatDate(entry.date)}</td>
                    <td className="py-3 px-4">
                      <Badge 
                        className={
                          entry.entry_type === "deposit" ? "bg-blue-500/20 text-blue-400" :
                          entry.entry_type === "withdrawal" ? "bg-purple-500/20 text-purple-400" :
                          entry.entry_type === "gain" ? "bg-emerald/20 text-emerald" :
                          "bg-red-500/20 text-red-400"
                        }
                      >
                        {entry.entry_type}
                      </Badge>
                    </td>
                    <td className={`py-3 px-4 text-right font-medium tabular-nums ${
                      entry.entry_type === "gain" ? "text-emerald" :
                      entry.entry_type === "loss" ? "text-red-400" :
                      entry.entry_type === "withdrawal" ? "text-purple-400" :
                      "text-white"
                    }`}>
                      {entry.entry_type === "loss" || entry.entry_type === "withdrawal" ? "-" : "+"}
                      {formatCurrency(entry.amount)}
                    </td>
                    <td className="py-3 px-4 text-platinum/60">{entry.description || "-"}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={4} className="py-12 text-center text-platinum/60">
                      No fund entries yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default FundManagement;
